import {
	DndContext,
	DragOverlay,
	PointerSensor,
	pointerWithin,
	useDndContext,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { DeleteIcon } from '@treams/treams-icons';
import { IconButton, Library, Tooltip } from '@treams/treams-ui';

import FeedbackQuestionCard, {
	FeedbackQuestionCardItem,
	FeedbackQuestionCardSaveValues,
} from '../../../../../feedback/settings/FeedbackQuestionCard';
import {
	ComposerQuestion,
	fromCardValues,
	toFeedbackQuestion,
} from './feedbackComposerQuestionAdapter';

const Ord = styled(Flex)`
	width: 32px;
	height: 32px;
	border-radius: ${(props) => props.theme.tokens.radius.full}px;
	border: 1px solid ${(props) => props.theme.tokens.border.subtle};
	align-items: center;
	justify-content: center;
	color: ${(props) => props.theme.tokens.text.primary};
`;

interface IProps {
	questions: ComposerQuestion[];
	readOnly?: boolean;
	autoEditQuestionID?: string;
	onChange: (questions: ComposerQuestion[]) => void;
}

function FeedbackComposerQuestionsDraggable(props: IProps) {
	const { t } = useTranslation();
	const { localize, language } = useLocalize();

	const { active } = useDndContext();

	// Convert to FeedbackQuestion with a stable identity per underlying question.
	// arrayMove preserves the ComposerQuestion object references across a reorder,
	// so caching on that identity keeps the item prop stable during a drag. This
	// mirrors the working template list and prevents framer-motion's layoutId
	// projection from re-reconciling mid-animation (which left the dragged card
	// stuck invisible after drop).
	const cacheRef = useRef(new WeakMap<ComposerQuestion, FeedbackQuestionCardItem>());

	useEffect(() => {
		cacheRef.current = new WeakMap<ComposerQuestion, FeedbackQuestionCardItem>();
	}, [language.id]);

	const toItem = useCallback(
		(question: ComposerQuestion) => {
			const cached = cacheRef.current.get(question);

			if (cached) {
				return cached;
			}

			const converted = toFeedbackQuestion(question, language.id);
			cacheRef.current.set(question, converted);

			return converted;
		},
		[language.id],
	);

	const handleSave =
		(question: ComposerQuestion) => async (values: FeedbackQuestionCardSaveValues) => {
			const updated = fromCardValues(values, question, localize);
			props.onChange(props.questions.map((item) => (item.id === question.id ? updated : item)));
		};

	const handleDelete = (question: ComposerQuestion) => () => {
		props.onChange(props.questions.filter((item) => item.id !== question.id));
	};

	return (
		<SortableContext items={props.questions.map((item) => `item:${item.id}`)} strategy={() => null}>
			{props.questions.map((question, index) => (
				<Box key={question.id} mb={2}>
					<Flex alignItems="center">
						<motion.div
							layoutId={active ? undefined : `ord:${question.id}`}
							transition={{ duration: 0.2, ease: 'circOut' }}
						>
							<Ord>{index + 1}</Ord>
						</motion.div>
						<Box flexGrow={1} flexShrink={1} ml={2}>
							<Library.Item.Draggable
								embeddedHandle
								item={toItem(question)}
								new={question.id === props.autoEditQuestionID}
								buttons={[
									// This one probably has the wrong type in Library.Item.Draggable
									// eslint-disable-next-line react/jsx-key
									<Tooltip
										content={t('settings.feedbackLibrary.template.questions.remove.button')}
										portal
									>
										<IconButton
											type="destructive"
											icon={<DeleteIcon />}
											onClick={handleDelete(question)}
										/>
									</Tooltip>,
								]}
								renderCard={(cardProps) => (
									<FeedbackQuestionCard {...cardProps} onSave={handleSave(question)} />
								)}
							/>
						</Box>
					</Flex>
				</Box>
			))}
			<DragOverlay dropAnimation={null} zIndex={10} style={{ cursor: 'grabbing' }}>
				{active && (
					<Library.Item
						embeddedHandle
						dragging
						active
						item={toItem(props.questions.find(({ id }) => `item:${id}` === active.id)!)}
						buttons={[
							// This one probably has the wrong type in Library.Item
							// eslint-disable-next-line react/jsx-key
							<Tooltip
								content={t('settings.feedbackLibrary.template.questions.remove.button')}
								portal
							>
								<IconButton type="destructive" icon={<DeleteIcon />} />
							</Tooltip>,
						]}
						renderCard={(cardProps) => <FeedbackQuestionCard {...cardProps} readOnly />}
					/>
				)}
			</DragOverlay>
		</SortableContext>
	);
}

export default function FeedbackComposerQuestions(props: IProps) {
	const { language } = useLocalize();
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
	);

	// Keep a local buffer for the questions so reordering during a drag stays
	// self-contained. Propagating every onDragOver up to the ancestor modal and
	// syncing it back down desyncs dnd-kit/framer-motion and leaves the dragged
	// item stuck invisible after drop. We only propagate on drag end.
	const [items, setItems] = useState(props.questions);
	const draggingRef = useRef(false);
	const itemsRef = useRef(items);
	itemsRef.current = items;

	useEffect(() => {
		if (!draggingRef.current) {
			setItems(props.questions);
		}
	}, [props.questions]);

	if (props.readOnly) {
		return (
			<Box>
				{props.questions.map((question, index) => (
					<Box key={question.id} mb={2}>
						<Flex alignItems="center">
							<Ord flexShrink={0}>{index + 1}</Ord>
							<Box flexGrow={1} flexShrink={1} ml={2}>
								<Library.Item
									embeddedHandle
									item={toFeedbackQuestion(question, language.id)}
									draggable={false}
									readOnly
									renderCard={(cardProps) => <FeedbackQuestionCard {...cardProps} readOnly />}
								/>
							</Box>
						</Flex>
					</Box>
				))}
			</Box>
		);
	}

	return (
		<DndContext
			collisionDetection={pointerWithin}
			modifiers={[restrictToVerticalAxis]}
			sensors={sensors}
			onDragStart={() => {
				draggingRef.current = true;
			}}
			onDragOver={(event) => {
				if (typeof event.active.id !== 'string' || typeof event.over?.id !== 'string') {
					return;
				}

				setItems((current) => {
					const source = current.findIndex(({ id }) => `item:${id}` === event.active.id);
					const destination = current.findIndex(({ id }) => `item:${id}` === event.over?.id);

					if (source === -1 || destination === -1 || source === destination) {
						return current;
					}

					return arrayMove(current, source, destination);
				});
			}}
			onDragEnd={() => {
				draggingRef.current = false;
				props.onChange(itemsRef.current);
			}}
			onDragCancel={() => {
				draggingRef.current = false;
				setItems(props.questions);
			}}
		>
			<FeedbackComposerQuestionsDraggable
				questions={items}
				autoEditQuestionID={props.autoEditQuestionID}
				onChange={props.onChange}
			/>
		</DndContext>
	);
}
